/**
 * NOTE: 年末調整（YearEndAdjustment）パッケージ一覧用 develop 向け hybrid mock 取得層。
 * バックエンド API 対応後は本ファイルごと削除し、`_packages/-query/index.ts` から
 * `@/features/operation-packages` の実 API を直接利用する。
 */
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { GovernmentApplicationAggregateTableRowData } from '@/features/labor-core/-components/government-application-aggregate/types';
import {
  getGovernmentApplicationPackageInstances as getGovernmentApplicationPackageInstancesApi,
  getOperationPackageCountInProgress as getOperationPackageCountInProgressApi,
  getOperationPackagePatterns as getOperationPackagePatternsApi,
  getOperationPackages as getOperationPackagesApi,
  getOperationTaskCountInProgress as getOperationTaskCountInProgressApi,
  type OperationPackageCountInProgressResponse,
  type OperationPackagePatternsResponse,
  type OperationPackagesResponse,
  type OperationTaskCountInProgressResponse,
  type OwnerProduct,
} from '@/features/operation-packages/api';
import { operationPackageQueryKeys } from '@/features/operation-packages/hooks';
import { mapGovernmentApplicationInstanceToTableRowData } from '@/features/operation-packages/mapGovernmentApplicationInstanceToTableRowData';
import { featureFlags } from '@/lib/env';

import {
  MOCK_YEA_COUNT_IN_PROGRESS,
  MOCK_YEA_GOVERNMENT_APPLICATION_INSTANCES,
  MOCK_YEA_OPERATION_PACKAGE,
  MOCK_YEA_PATTERN_TASK_COUNT,
  MOCK_YEA_PATTERNS,
  type MockOperationPackagesResponse,
} from '../-mock/yearEndAdjustmentFixtures';
import {
  isMockYeaOperationPackageId,
  isMockYeaPatternId,
} from '../../-shared/yearEndAdjustmentMockIds';

export type { MockOperationPackagesResponse };

const OWNER_PRODUCT = 'HRF_LABOR_CORE' as const satisfies OwnerProduct;

type GovernmentApplicationInstancesData = {
  items: GovernmentApplicationAggregateTableRowData[];
  count: number;
  maxPage: number;
  length: number;
  page: number;
};

/** develop バックエンドは YearEndAdjustment を返さないため、実 API 一覧に FE mock を追加する */
function appendFrontendYeaOperationPackage(
  packages: OperationPackagesResponse,
): MockOperationPackagesResponse {
  if (
    packages.some((pkg) => isMockYeaOperationPackageId(pkg.operationPackageId))
  ) {
    return packages as MockOperationPackagesResponse;
  }
  return [...packages, MOCK_YEA_OPERATION_PACKAGE];
}

/**
 * NOTE: 実 API 確定後は差し替える（develop 向け: 実一覧 + FE 追加 YEA）。
 * `ENABLE_YEAR_END_ADJUSTMENT_OPERATION_PACKAGE` が無効なセッションでは FE mock を注入せず実 API をそのまま返す。
 */
export const getOperationPackages =
  async (): Promise<MockOperationPackagesResponse> => {
    if (!featureFlags.check('ENABLE_YEAR_END_ADJUSTMENT_OPERATION_PACKAGE')) {
      return getOperationPackagesApi(OWNER_PRODUCT);
    }
    try {
      const real = await getOperationPackagesApi(OWNER_PRODUCT);
      return appendFrontendYeaOperationPackage(real);
    } catch {
      return structuredClone([MOCK_YEA_OPERATION_PACKAGE]);
    }
  };

/** NOTE: 実 API 確定後は差し替える */
export const getOperationPackageCountInProgress = async (
  id: string,
): Promise<OperationPackageCountInProgressResponse> => {
  if (isMockYeaOperationPackageId(id)) {
    return structuredClone(MOCK_YEA_COUNT_IN_PROGRESS);
  }
  return getOperationPackageCountInProgressApi(OWNER_PRODUCT, id);
};

/** NOTE: 実 API 確定後は差し替える */
export const getOperationPackagePatterns = async (
  id: string,
): Promise<OperationPackagePatternsResponse> => {
  if (isMockYeaOperationPackageId(id)) {
    return structuredClone(MOCK_YEA_PATTERNS);
  }
  return getOperationPackagePatternsApi(OWNER_PRODUCT, id);
};

/** NOTE: 実 API 確定後は差し替える */
export const useGetOperationPackages = (ownerProduct: OwnerProduct) => {
  const { data, isLoading, error } = useQuery({
    queryKey: operationPackageQueryKeys.list(ownerProduct),
    queryFn: () => getOperationPackages(),
  });
  return { data, isLoading, error };
};

/** NOTE: 実 API 確定後は差し替える */
export const useGetOperationPackagePatterns = (
  ownerProduct: OwnerProduct,
  id: string,
  options?: { enabled?: boolean },
) => {
  const { data, isLoading, error } = useSuspenseQuery({
    queryKey: operationPackageQueryKeys.patterns(ownerProduct, id),
    queryFn: () => {
      if (options?.enabled === false || !id) {
        return Promise.resolve([]);
      }
      return getOperationPackagePatterns(id);
    },
  });
  return { data, isLoading, error };
};

/** NOTE: 実 API 確定後は差し替える */
export const useGetGovernmentApplicationPackageInstances = (
  ownerProduct: OwnerProduct,
  patternId: string,
  options?: {
    enabled?: boolean;
    page?: number;
    pageSize?: number;
    targetId?: string;
  },
) => {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const targetId = options?.targetId;
  const { data, isPending, error } = useQuery({
    queryKey: operationPackageQueryKeys.governmentApplicationInstances(
      ownerProduct,
      patternId,
      page,
      pageSize,
      targetId,
    ),
    queryFn: async (): Promise<GovernmentApplicationInstancesData> => {
      if (isMockYeaPatternId(patternId)) {
        return structuredClone(MOCK_YEA_GOVERNMENT_APPLICATION_INSTANCES);
      }
      const response = await getGovernmentApplicationPackageInstancesApi(
        ownerProduct,
        patternId,
        { page, pageSize, targetId },
      );
      return {
        ...response,
        items: response.items.map((row) =>
          mapGovernmentApplicationInstanceToTableRowData(row),
        ),
      };
    },
    enabled: options?.enabled ?? true,
  });
  return { data, isPending, error };
};

/** NOTE: 実 API 確定後は差し替える */
export const useGetOperationTaskCountInProgress = ({
  ownerProduct,
  operationPackageId: id,
  patternId,
}: {
  ownerProduct: OwnerProduct;
  operationPackageId: string;
  patternId: string;
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: operationPackageQueryKeys.patternCountInProgress(
      ownerProduct,
      id,
      patternId,
    ),
    queryFn: (): Promise<OperationTaskCountInProgressResponse> => {
      if (isMockYeaOperationPackageId(id) && isMockYeaPatternId(patternId)) {
        return Promise.resolve(structuredClone(MOCK_YEA_PATTERN_TASK_COUNT));
      }
      return getOperationTaskCountInProgressApi(ownerProduct, id, patternId);
    },
  });
  return { data, isLoading, error };
};
